<script setup lang="ts">
import type { LucideIcon } from 'lucide-vue-next'
import Textarea from 'primevue/textarea'
import { computed, onMounted, ref, watch } from 'vue'
import ModerationErrors from './ModerationErrors.vue'

import { useModerationStore } from '@/stores/moderationStore'
import type { ModerationError } from '@/types'

interface Props {
	title: string
	icon: LucideIcon
	placeholder?: string
	info?: string
	textLimit?: boolean
	limitMessage?: string
	disabled?: boolean
	useLargerFont?: boolean
	isBlinking?: boolean
	type: 'positive' | 'negative'
	mode?: 'tweak' | 'generator'
}

const props = withDefaults(defineProps<Props>(), {
	placeholder: '',
	info: '',
	textLimit: false,
	limitMessage: 'Lengthy prompt',
	disabled: false,
	useLargerFont: false,
	isBlinking: false,
	type: 'positive',
	mode: 'generator',
})

const emit = defineEmits(['update:modelValue'])
const model = defineModel<string>()

const cssTextarea = {
	root: {
		class: '!bg-slate-900 !rounded font-medium',
	},
}

const moderationStore = useModerationStore()
const moderationErrors = ref<ModerationError<Array<string[]> | string[]>[]>([])
const inputValue = computed({
	get: () => model.value,
	set: (value) => {
		if (typeof value === 'string') {
			checkModeration(value)
		}
		emit('update:modelValue', value)
	},
})

watch(() => inputValue.value, () => {
	if (typeof inputValue.value === 'string' && inputValue.value !== '') {
		checkModeration(inputValue.value)
	}
}, { immediate: true })

async function checkModeration(prompt: string): Promise<void> {
	moderationErrors.value = moderationStore.checkPrompt(prompt, props.type)
}

/* onMounted(() => {
	if (typeof inputValue.value === 'string' && inputValue.value !== '') {
		checkModeration(inputValue.value)
	}
}) */
</script>

<template>
	<div class="prompt">
		<div class="header">
			<div class="flex items-center gap-3">
				<div class="flex-center w-7 h-7 rounded-lg bg-slate-700/50">
					<component
						:is="icon"
						class="lucide-button-icon text-purple-400"
					/>
				</div>
				<span class="text-slate-200 font-medium text-sm md:text-base">{{ title }}</span>
			</div>

			<div class="flex items-center gap-2">
				<slot name="header-actions" />
			</div>
		</div>

		<Textarea
			v-model="inputValue"
			auto-resize
			class="prompt-input"
			:class="{
				'animate-blink': isBlinking,
				'text-base': useLargerFont,
				'text-sm': !useLargerFont,
			}"
			:pt="cssTextarea"
			:placeholder="placeholder"
			:disabled="disabled"
			spellcheck="false"
		/>

		<span
			v-if="textLimit"
			class="text-xsp font-medium text-orange-300"
		>{{ limitMessage }}</span>

		<span
			v-if="info"
			class="prompt-info small-info"
		>{{ info }}</span>

		<!-- Footer actions slot -->
		<slot name="footer-actions" />

		<!-- Composant de modération -->
		<ModerationErrors
			:errors="moderationErrors"
			:mode="props.mode"
		/>
	</div>
</template>

<style scoped lang="scss">
.p-inputtextarea {
  resize: vertical !important;
}

.prompt {
  @apply flex flex-col bg-slate-950/10 rounded-xl w-full gap-3 px-3 py-4;

  & > .prompt-input {
    @apply w-full h-[80px] leading-relaxed;
  }

  & > .prompt-info {
    @apply mt-[1px];
  }
}

.header {
  @apply flex items-center justify-between w-full border-b border-slate-700/60;
}

.small-info {
  @apply text-xsp text-slate-300;
}

@keyframes blink {
  0%, 100% { @apply border-slate-500 }
  50% { @apply border-yellow-500 }
}

.animate-blink {
  animation: blink 0.2s linear 3;
}
</style>
